import { Button } from "../ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import {
  MoreHorizontal,
  KeyRound,
  LogOut,
  Ban,
  Trash2,
  Check,
  Loader2,
  Pencil,
  Calendar,
  RotateCcw,
  UserCheck,
} from "lucide-react";
import type { ManagedUser, AccountRole, ConvertibleAccountType } from "./types";
import {
  ALL_ACCOUNT_ROLES,
  canOfferSupportLogin,
  isUserBanned,
} from "./types";
import type { Trial } from "./trials-api";

type UserActionsMenuProps = {
  user: ManagedUser;
  isLoading: boolean;
  trial?: Trial | null;
  onEdit: (user: ManagedUser) => void;
  onResetPassword: (user: ManagedUser) => void;
  onRevokeSessions: (user: ManagedUser) => void;
  /** Absent when the caller may not create support logins. */
  onSupportLogin?: (user: ManagedUser) => void;
  onAccountRoleChange: (userId: string, role: AccountRole) => void;
  /** `user` only, for a demo account. Demo comes from Manage trial, admin from Dashboard IAM. */
  onAccountTypeChange: (userId: string, type: ConvertibleAccountType) => void;
  onBanToggle: (user: ManagedUser) => void;
  onDelete: (user: ManagedUser) => void;
  onStartTrial?: (user: ManagedUser) => void;
  onManageTrial?: (user: ManagedUser, trial: Trial) => void;
  onReactivateTrial?: (user: ManagedUser) => void;
};

export const UserActionsMenu = ({
  user,
  isLoading,
  trial,
  onEdit,
  onResetPassword,
  onRevokeSessions,
  onSupportLogin,
  onAccountRoleChange,
  onAccountTypeChange,
  onBanToggle,
  onDelete,
  onStartTrial,
  onManageTrial,
  onReactivateTrial,
}: UserActionsMenuProps) => {
  const banned = isUserBanned(user);
  const trialStatus = trial?.status ?? null;
  const showManageTrial = Boolean(onManageTrial) || Boolean(onStartTrial);
  const showReactivate =
    trial &&
    (trialStatus === "expired" || trialStatus === "revoked") &&
    Boolean(onReactivateTrial);
  const showTrialSection = showManageTrial || showReactivate;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          disabled={isLoading}
        >
          {isLoading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <MoreHorizontal className="h-4 w-4" />
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>Actions</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => onEdit(user)} className="gap-2">
          <Pencil className="h-4 w-4" />
          Edit User
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => onResetPassword(user)}
          className="gap-2"
        >
          <KeyRound className="h-4 w-4" />
          Reset Password
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => onRevokeSessions(user)}
          className="gap-2"
        >
          <LogOut className="h-4 w-4" />
          Revoke Sessions
        </DropdownMenuItem>
        {/* Hidden for admins: et-auth refuses those targets on both the issue
            and the redeem paths, so the item would only ever produce a dead
            click. Presentation only; the gate is server-side. */}
        {onSupportLogin && canOfferSupportLogin(user) ? (
          <DropdownMenuItem
            onClick={() => onSupportLogin(user)}
            className="gap-2"
          >
            <UserCheck className="h-4 w-4" />
            Create support login link…
          </DropdownMenuItem>
        ) : null}
        {showTrialSection ? (
          <>
            <DropdownMenuSeparator />
            {showManageTrial ? (
              <DropdownMenuItem
                onClick={() =>
                  trial ? onManageTrial?.(user, trial) : onStartTrial?.(user)
                }
                className="gap-2"
              >
                <Calendar className="h-4 w-4" />
                Manage trial…
              </DropdownMenuItem>
            ) : null}
            {showReactivate ? (
              <DropdownMenuItem
                onClick={() => onReactivateTrial?.(user)}
                className="gap-2"
              >
                <RotateCcw className="h-4 w-4" />
                Reactivate trial…
              </DropdownMenuItem>
            ) : null}
          </>
        ) : null}
        <DropdownMenuSeparator />
        <DropdownMenuLabel className="text-xs text-muted-foreground">
          Roles
        </DropdownMenuLabel>
        {ALL_ACCOUNT_ROLES.map((role) => {
          const active = user.account_role === role;
          return (
            <DropdownMenuItem
              key={role}
              onClick={() => onAccountRoleChange(user.id, role)}
              className="gap-2"
            >
              {active ? (
                <Check className="h-4 w-4 text-primary" />
              ) : (
                <div className="h-4 w-4" />
              )}
              {role}
            </DropdownMenuItem>
          );
        })}
        {/* An account becomes demo only through Manage trial, which starts the
            trial in the same step. Type offers the way back: a demo account
            becomes a regular user, and ai-tutor removes its trial. */}
        {user.account_type === "demo" ? (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-xs text-muted-foreground">
              Type
            </DropdownMenuLabel>
            <DropdownMenuItem
              onClick={() => onAccountTypeChange(user.id, "user")}
              className="gap-2"
            >
              <div className="h-4 w-4" />
              Convert to user
            </DropdownMenuItem>
          </>
        ) : null}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={() => onBanToggle(user)}
          className={`gap-2 ${
            banned ? "" : "text-lime-700 focus:text-lime-700"
          }`}
        >
          <Ban className="h-4 w-4" />
          {banned ? "Unban User" : "Ban User"}
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => onDelete(user)}
          className="gap-2 text-destructive focus:text-destructive"
        >
          <Trash2 className="h-4 w-4" />
          Delete User
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
